import {
  Flex,
  HStack,
  IconButton,
  Spacer,
  Spinner,
  Text,
} from '@chakra-ui/react';
import dynamic from 'next/dynamic';
import { FC, useState } from 'react';
import {
  MdClose,
  MdDownload,
  MdOpenInNew,
  MdZoomIn,
  MdZoomOut,
} from 'react-icons/md';

import { ImageViewer } from './ImageViewer';

// Dynamically imported with SSR disabled — react-pdf relies on browser APIs
// (canvas, Worker) that are unavailable server-side.
const PdfDocumentViewer = dynamic(
  () =>
    import('@web-app/components/PdfDocumentViewer/PdfDocumentViewer').then(
      (mod) => mod.PdfDocumentViewer,
    ),
  {
    ssr: false,
    loading: () => (
      <Flex flex={1} align="center" justify="center">
        <Spinner size="lg" color="interactive.brand.default" />
      </Flex>
    ),
  },
);

const ZOOM_STEP = 0.25;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;

interface LabDocumentPreviewProps {
  filename: string;
  mimeType: string;
  downloadUrl: string | undefined;
  isLoadingUrl?: boolean;
  onDismiss: () => void;
  onDownload: () => void;
  onOpenInNewTab: () => void;
  onLoaded?: () => void;
}

/**
 * Inline preview for a single document (PDF via react-pdf, images via <img>).
 *
 * Used by any in-session surface that shows a presigned S3 document: the "Labs"
 * tab (LabDocumentsTab) and the Walmart shopping insights banner. The parent
 * connected component owns all state and passes a presigned S3 download URL
 * that expires after ~5 minutes.
 *
 * Layout (top → bottom):
 *  - Sticky header: back chevron (returns to list), truncated filename,
 *    zoom out, zoom in (PDF only), download + open-in-new-tab buttons.
 *  - Content area: fills remaining height with the document, a spinner
 *    while the URL is loading, or an error/unsupported-type message.
 *
 * This component is purely presentational — it has no knowledge of Jotai
 * atoms, tRPC, or presigned-URL expiry logic.
 */
export const LabDocumentPreview: FC<LabDocumentPreviewProps> = ({
  filename,
  mimeType,
  downloadUrl,
  isLoadingUrl = false,
  onDismiss,
  onDownload,
  onOpenInNewTab,
  onLoaded,
}) => {
  const [scale, setScale] = useState<number>(1);

  const isPdf = mimeType === 'application/pdf';

  const zoomIn = () => setScale((prev) => Math.min(prev + ZOOM_STEP, ZOOM_MAX));
  const zoomOut = () =>
    setScale((prev) => Math.max(prev - ZOOM_STEP, ZOOM_MIN));

  const renderContent = () => {
    if (isLoadingUrl) {
      return (
        <Flex flex={1} align="center" justify="center">
          <Spinner size="lg" color="interactive.brand.default" />
        </Flex>
      );
    }

    if (!downloadUrl) {
      return (
        <Flex flex={1} align="center" justify="center">
          <Text fontSize="sm" color="text.muted">
            Unable to load document
          </Text>
        </Flex>
      );
    }

    if (isPdf) {
      return (
        <PdfDocumentViewer
          url={downloadUrl}
          scale={scale}
          onLoaded={onLoaded}
        />
      );
    }

    if (mimeType.startsWith('image/')) {
      return <ImageViewer url={downloadUrl} filename={filename} />;
    }

    return (
      <Flex flex={1} align="center" justify="center">
        <Text fontSize="sm" color="text.muted">
          Preview not available for this file type
        </Text>
      </Flex>
    );
  };

  return (
    <Flex direction="column" height="100%" width="100%" bg="bg.default">
      {/* Header bar */}
      <HStack
        spacing={2}
        px={3}
        py={2}
        borderBottomWidth="1px"
        borderBottomColor="border.default"
        bg="bg.default"
        position="sticky"
        top={0}
        zIndex={1}
        flexShrink={0}
      >
        <Text
          fontSize="sm"
          fontWeight={500}
          color="text.default"
          isTruncated
          title={filename}
          minW={0}
        >
          {filename}
        </Text>
        <Spacer />
        <HStack spacing={1} flexShrink={0}>
          {isPdf && (
            <>
              <IconButton
                aria-label="Zoom out"
                icon={<MdZoomOut size={18} />}
                variant="ghost"
                size="sm"
                color="text.muted"
                onClick={zoomOut}
                isDisabled={scale <= ZOOM_MIN}
              />
              <IconButton
                aria-label="Zoom in"
                icon={<MdZoomIn size={18} />}
                variant="ghost"
                size="sm"
                color="text.muted"
                onClick={zoomIn}
                isDisabled={scale >= ZOOM_MAX}
              />
            </>
          )}
          <IconButton
            aria-label="Download document"
            icon={<MdDownload size={18} />}
            variant="ghost"
            size="sm"
            color="text.muted"
            onClick={onDownload}
            isDisabled={!downloadUrl}
          />
          <IconButton
            aria-label="Open in new tab"
            icon={<MdOpenInNew size={18} />}
            variant="ghost"
            size="sm"
            color="text.muted"
            onClick={onOpenInNewTab}
            isDisabled={!downloadUrl}
          />
        </HStack>
        <IconButton
          aria-label="Close preview"
          icon={<MdClose size={18} />}
          variant="ghost"
          size="sm"
          color="text.default"
          onClick={onDismiss}
          ml={2}
        />
      </HStack>

      {/* Content area */}
      {renderContent()}
    </Flex>
  );
};

LabDocumentPreview.displayName = 'LabDocumentPreview';
